import { CELLS_REACH, COLLISION_CELLS_PER_TILE, type EntityInstance, type JsonSchema, type JsonValue } from "daedalus-core";
import { SLUG_PATTERN } from "../content/slug.ts";
import { MAX_CELL_OFFSET, NPC_TEMPLATE_LOOKUP, type NpcCell } from "../npcs/marker.ts";

/**
 * The server-only `field` marker (ADR-144 decision 6, as amended): a field is a marker whose painted cells are a
 * stretch of farmland, either rental patches its farm NPC lets out or a shared field anyone may plant in. Pure: the
 * editor's Issues list and the compile read it the same way. Checks that need the map (the shape of a rental, an
 * indoor map) are in `field-map.ts`.
 */

/**
 * The kinds of field: patches rented one by one from the farm NPC, or open ground shared by everyone.
 */
export const FIELD_KINDS = ["rental", "shared"] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

/**
 * The patch size of a rental field that sets none: 3 by 3 crop cells.
 */
export const DEFAULT_PATCH_SIZE = 3;

/**
 * The most crop cells a patch is wide and tall.
 */
export const MAX_PATCH_SIZE = 6;

/**
 * The most collision cells a field's painted area holds: every cell of the window the editor lets an area be painted
 * in, `CELLS_REACH` tiles past the marker's own tile on every side.
 */
export const MAX_FIELD_CELLS = (1 + 2 * CELLS_REACH) * (1 + 2 * CELLS_REACH) * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;

/**
 * The marker's fields as the editor's inspector draws them. `patchSize` is read for a rental field only.
 */
export const FIELD_SCHEMA: JsonSchema = {
  type: "object",
  properties: {
    kind: { type: "string", title: "Kind", enum: [...FIELD_KINDS] },
    farmNpc: { type: "string", title: "Farm NPC", lookup: NPC_TEMPLATE_LOOKUP },
    patchSize: { type: "integer", title: "Patch size", default: DEFAULT_PATCH_SIZE, minimum: 1 },
    cells: { type: "cells", title: "Field" },
  },
  required: ["kind", "farmNpc", "cells"],
};

/**
 * A field marker's fields with the patch size filled.
 */
export interface FieldFields {
  readonly kind: FieldKind;
  readonly farmNpc: string;
  readonly patchSize: number;
  readonly cells: readonly NpcCell[];
}

/**
 * The verdict on one marker's fields; each error is the sentence after the marker's name.
 */
export type FieldFieldsVerdict = { readonly ok: true; readonly field: FieldFields } | { readonly ok: false; readonly errors: readonly string[] };

/**
 * Whether a field is left empty: absent or null.
 *
 * @param value The field.
 * @returns True when empty.
 */
function empty(value: JsonValue | undefined): value is null | undefined {
  return value === undefined || value === null;
}

/**
 * The cells a value holds, each a pair of integers, or null when it is not a list of them.
 *
 * @param value The value.
 * @returns The cells in the order given.
 */
function cellsOf(value: JsonValue): NpcCell[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const cells: NpcCell[] = [];
  for (const pair of value) {
    if (!Array.isArray(pair) || pair.length !== 2 || !Number.isInteger(pair[0]) || !Number.isInteger(pair[1])) {
      return null;
    }
    cells.push([pair[0] as number, pair[1] as number]);
  }
  return cells;
}

/**
 * Reads and checks a field marker's fields: a kind, a farm NPC template, a patch size and the painted cells.
 *
 * @param fields The marker's fields.
 * @returns The fields with the patch size filled, or every error in the schema's order.
 */
export function readFieldFields(fields: { readonly [key: string]: JsonValue }): FieldFieldsVerdict {
  const errors: string[] = [];
  const kind = fields["kind"];
  if (empty(kind) || kind === "") {
    errors.push("names no kind; choose rental or shared");
  } else if (!(FIELD_KINDS as readonly JsonValue[]).includes(kind)) {
    errors.push(`is of the kind ${String(kind)}; use rental or shared`);
  }
  const farmNpc = fields["farmNpc"];
  if (typeof farmNpc !== "string" || farmNpc === "") {
    errors.push("names no farm NPC; choose an NPC template");
  } else if (!SLUG_PATTERN.test(farmNpc)) {
    errors.push(`names the farm NPC ${farmNpc}; use lower-case letters, digits and -`);
  }
  const patchSize = fields["patchSize"];
  if (!empty(patchSize) && !(typeof patchSize === "number" && Number.isInteger(patchSize) && patchSize >= 1 && patchSize <= MAX_PATCH_SIZE)) {
    errors.push(`has a patch size of ${String(patchSize)}; use a whole number from 1 to ${MAX_PATCH_SIZE}`);
  }
  const painted = fields["cells"];
  const cells = empty(painted) ? [] : cellsOf(painted);
  if (cells === null) {
    errors.push("has cells that are not a list of [column, rowsUp] pairs of whole numbers");
  } else if (cells.length === 0) {
    errors.push("has no cells; paint at least one");
  } else if (cells.length > MAX_FIELD_CELLS) {
    errors.push(`has ${cells.length} cells; keep it to ${MAX_FIELD_CELLS}`);
  } else if (cells.some((cell) => Math.abs(cell[0]) > MAX_CELL_OFFSET || Math.abs(cell[1]) > MAX_CELL_OFFSET)) {
    errors.push(`has a cell outside ${MAX_CELL_OFFSET} of its marker; keep every cell within ${MAX_CELL_OFFSET} on each axis`);
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, field: { kind: kind as FieldKind, farmNpc: farmNpc as string, patchSize: empty(patchSize) ? DEFAULT_PATCH_SIZE : (patchSize as number), cells: cells as NpcCell[] } };
}

/**
 * The marker's placement on its map, the tile its point names.
 */
export type FieldMarker = Pick<EntityInstance, "id" | "x" | "y">;
